/* eslint-disable max-classes-per-file */
import axios from 'axios'
import { Tenant } from '@/types'
import { client } from '@/services/beef'
import { getUserToken } from './check-session'
import { getAuthXToken } from './authx-token-payload'

/**
  Passkey enrolment via the Auth0 My Account API (no Universal Login redirect).

  Flow:
    1. getUserToken()                          -> a `/me/` user access token (scope: create:me:authentication_methods)
    2. POST /me/v1/authentication-methods      -> WebAuthn creation options + auth_session
    3. navigator.credentials.create()          -> the device builds the passkey (browser-only)
    4. POST /me/v1/authentication-methods/{id}/verify -> Auth0 stores the passkey

  The authx backend is bypassed for this flow — the WebAuthn ceremony runs
  entirely in the browser, like the Universal Login enrolment it replaces.
  The authx `passkeys` DB mirror row is created separately, via `POST /passkeys`
  (the beef's `createPasskey`), once this resolves.
* */

export class PasskeyCreationCancelledError extends Error {
  constructor() {
    super('Passkey creation cancelled')
    this.name = 'PasskeyCreationCancelledError'
  }
}

export class PasskeyAlreadyExistError extends Error {
  constructor() {
    super('Passkey already exists for this device')
    this.name = 'PasskeyAlreadyExistError'
  }
}

// ---- base64url <-> ArrayBuffer helpers (WebAuthn transports values as base64url) ----

const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// The My Account API returns challenge / user.id / excludeCredentials[].id as base64url
// strings; the WebAuthn API needs them as ArrayBuffers.
const toCreationOptions = (publicKey: any): any => ({
  ...publicKey,
  challenge: base64UrlToBuffer(publicKey.challenge),
  user: {
    ...publicKey.user,
    id: base64UrlToBuffer(publicKey.user.id),
  },
  excludeCredentials: (publicKey.excludeCredentials || []).map((cred: any) => ({
    ...cred,
    id: base64UrlToBuffer(cred.id),
  })),
})

/**
  Shape the browser's attestation credential back into the JSON the verify step expects.

  Follows WebAuthn's standard registration-response JSON shape (the same one
  `PublicKeyCredential.toJSON()` produces): `transports` sits inside `response`,
  `authenticatorAttachment` at the top level.

  Both are optional and not implemented by every browser, so they are included only
  when actually present — sending `null` or `[]` is worse than omitting them, since
  Auth0 stores `transports` on the authentication method and an empty value would
  overwrite a useful hint with nothing.

  `transports` tells the browser which transport to prefer at login time (internal /
  hybrid / usb / nfc / ble). Without it the login-time picker has no hint.
* */
const toAuthnResponse = (credential: any) => {

  const { response } = credential

  // getTransports() lives on AuthenticatorAttestationResponse and is absent on
  // older browsers, so feature-detect rather than call it blind.
  const transports: string[] = typeof response.getTransports === 'function'
    ? response.getTransports() || []
    : []

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    ...(credential.authenticatorAttachment
      ? { authenticatorAttachment: credential.authenticatorAttachment }
      : {}),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      ...(transports.length ? { transports } : {}),
    },
  }
}

// The user's existing passkeys, via login-beef → auth-api. A failed lookup must not block
// enrolment — it only loses the duplicate guard.
const getExistingPasskeyCredentials = async () => {
  try {
    const { credentials } = await client.getPasskeyCredentials({
      body: { token: getAuthXToken().value },
    })
    return credentials
  } catch {
    return []
  }
}

export const enrollPasskey = async (tenant: Tenant): Promise<void> => {

  const accessToken = await getUserToken(tenant)

  const base = `https://${tenant.loginDomain}/me/v1/authentication-methods`
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  }

  // 1. Initiate enrolment — returns WebAuthn creation options + an auth_session.
  //    In parallel, fetch the user's existing passkey credentials from authx (login-beef →
  //    auth-api) — the init response does not carry excludeCredentials (SS-7534).
  //    `connection` / `identity_user_id` may be required depending on the tenant;
  //    if this 400s, inspect the error and add them (see the My Account API docs).
  const [{ data: init }, existingCredentials] = await Promise.all([
    axios.post(base, {
      type: 'passkey',
      use_exclude_credentials: true,
    }, { headers }),
    getExistingPasskeyCredentials(),
  ])

  const publicKey = init.authn_params_public_key
  const initExcluded = publicKey.excludeCredentials || []
  const excludeCredentials = [
    ...initExcluded,
    ...existingCredentials.filter(cred => !initExcluded.some((excluded: any) => excluded.id === cred.id)),
  ]

  // 2. Run the WebAuthn ceremony in the browser. Requires transient user activation —
  //    callers must keep this close to the click that triggered enrolment.
  let credential: any = null
  try {
    credential = (await navigator.credentials.create({
      publicKey: toCreationOptions({ ...publicKey, excludeCredentials }),
    })) as any
  } catch (error) {
    // the authenticator already holds a credential in excludeCredentials — the
    // user is trying to re-enroll a passkey they already have (SS-7534)
    if ((error as Error)?.name === 'InvalidStateError') {
      throw new PasskeyAlreadyExistError()
    }
    throw error
  }

  if (!credential) {
    throw new PasskeyCreationCancelledError()
  }

  // 3. Verify — Auth0 persists the passkey against the user.
  //    For a new passkey the path id is `passkey|new` (per the My Account API);
  //    prefer whatever the init response returns, encoded for the `|`.
  const methodId = init.id ?? 'passkey|new'

  const authnResponse = toAuthnResponse(credential)

  await axios.post(
    `${base}/${encodeURIComponent(methodId)}/verify`,
    {
      auth_session: init.auth_session,
      authn_response: authnResponse,
    },
    { headers },
  )
}
